import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { useTheme } from "next-themes"
import { useEffect, useState } from "react"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { Card } from "../../ui/card"
import { useChartColors } from "../../../hooks/useChartColors"
import CustomerLoader from "../../../components/loaders/CustomerLoader"
import { useWholesaleAverageOrderValue } from "../hooks/useWholesaleAOV"
import { useWholesalePurchaseFrequency } from "../hooks/useWholesalePurchaseFreq"
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react"
import { cn } from "../../../utils/utils"
import { formatCurrency } from "../../../utils/formatCurrency"



export function WholesaleAOVFrequency() {
  const {
    data: {
      chartData: aovData = [],
      currentAOV = 0,
      highestMonth = { year: new Date().getFullYear(), month: "", value: 0 },
      yoyChange = 0,
      lastYearAvg = 0
    } = {},
    isLoading
  } = useWholesaleAverageOrderValue();

  const { data: frequency, isLoading: isFrequencyLoading } = useWholesalePurchaseFrequency();
  const frequencyData = frequency?.purchase_frequency;


  const frequencyDataArray =
    frequency?.purchase_frequency
      ? Object.entries(frequency.purchase_frequency).map(([_, value]) => ({
        frequency: value.status,
        customers: value.customers,
      }))
      : [];
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const currentYear = new Date().getFullYear()
  const lastYear = currentYear - 1

  const colors = useChartColors("wholesale")
  if (!colors) return null;

  const chartColors = {
    grid: mounted && theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
    text: mounted && theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
    tooltip: {
      bg: mounted && theme === "dark" ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.8)",
      border: mounted && theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
      text: mounted && theme === "dark" ? "#ffffff" : "#000000",
    },
  }

  return (
    <>
      <SummaryHeader title="Avg. Order Value & Purchase Frequency" />
      <Card>
        <Tabs defaultValue="aov">
          <TabsList className="mb-4">
            <TabsTrigger value="aov">Average Order Value</TabsTrigger>
            <TabsTrigger value="frequency">Purchase Frequency</TabsTrigger>
          </TabsList>

          <TabsContent value="aov">
            {isLoading ? (
              <CustomerLoader variant="customer-chart" />
            ) : (
              <>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={aovData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: chartColors.grid }}                      
                      />
                      <YAxis
                        tick={{ fill: "var(--chart-text)" }}
                        axisLine={{ stroke: chartColors.grid }}
                        tickFormatter={(value) => `$${value}`} />
                      <Tooltip
                        formatter={(value) => [`$${value}`, "AOV"]}
                        contentStyle={{
                          backgroundColor: chartColors.tooltip.bg,
                          border: `1px solid ${chartColors.tooltip.border}`,
                          color: chartColors.tooltip.text,
                        }}
                        labelStyle={{ color: chartColors.tooltip.text }} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey={currentYear.toString()}
                        name={`${currentYear} AOV`}
                        stroke={colors[0]}
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }} />
                      <Line
                        type="monotone"
                        dataKey={lastYear.toString()}
                        name={`${lastYear} AOV`}
                        stroke={colors[5]}
                        strokeWidth={3}
                        dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Current AOV</div>
                    <div className="text-xl font-semibold mt-1">{formatCurrency(currentAOV)}</div>
                    <div className="mt-2 flex items-center text-xs">
                      {yoyChange > 0 ? (
                        <ArrowUpIcon className="mr-1 h-3 w-3 text-emerald-500" />
                      ) : (
                        <ArrowDownIcon className="mr-1 h-3 w-3 text-rose-500" />
                      )}
                      <span className={cn(yoyChange > 0 ? "text-emerald-500" : "text-rose-500")}>
                        {Math.abs(yoyChange).toFixed(2)}% YoY
                      </span>
                    </div>
                  </div>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Current Highest AOV Month</div>
                    <div className="text-xl font-semibold mt-1">{highestMonth.month} {highestMonth.year}</div>
                    <div className="text-sm text-muted-foreground mt-1">{formatCurrency(highestMonth.value)}</div>
                  </div>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">YTD Average</div>
                    <div className="text-xl font-semibold mt-1">{formatCurrency(lastYearAvg ?? 0)}</div>
                    <div className="text-sm text-muted-foreground mt-1">Across all segments</div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="frequency">
            {isFrequencyLoading || !frequencyData ? (
              <CustomerLoader variant="purchase-freq" />
            ) : (
              <>
                <div>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={frequencyDataArray} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis dataKey="frequency" tick={{ fill: "var(--chart-text)" }} axisLine={{ stroke: chartColors.grid }} />
                        <YAxis tick={{ fill: "var(--chart-text)" }} axisLine={{ stroke: chartColors.grid }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: chartColors.tooltip.bg,
                            border: `1px solid ${chartColors.tooltip.border}`,
                            color: chartColors.tooltip.text,
                          }}
                        />
                        <Legend />
                        <Bar dataKey="customers" name="Number of Customers" fill={colors[6]} radius={[4, 4, 0, 0]} >
                          {frequencyDataArray.map((_entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={colors[index % colors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">One-Time Buyers</div>
                    <div className="text-xl font-semibold mt-1">
                      {frequencyData.one_time_buyers.percentage}%
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {frequencyData.one_time_buyers.customers.toLocaleString()} customers
                    </div>
                  </div>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Repeat Customers</div>
                    <div className="text-xl font-semibold mt-1">
                      {frequencyData.repeat_customers.percentage}%
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {frequencyData.repeat_customers.customers.toLocaleString()} customers
                    </div>
                  </div>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <div className="text-sm text-muted-foreground">Power Users (7+ purchases)</div>
                    <div className="text-xl font-semibold mt-1">
                      {frequencyData.power_users.percentage}%
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {frequencyData.power_users.customers.toLocaleString()} customers
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

        </Tabs>
      </Card>
    </>
  )
}
